import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Lock, ShieldCheck } from "lucide-react";
import type { VaultStatus } from "@/hooks/useMindfulVault";

interface Props {
  open: boolean;
  status: VaultStatus;
  onSetup: (passphrase: string) => Promise<void>;
  onUnlock: (passphrase: string) => Promise<void>;
  onReset: (passphrase: string) => Promise<void>;
  onSkip: () => void;
  onClose: () => void;
}

const MindfulVaultDialog: React.FC<Props> = ({ open, status, onSetup, onUnlock, onReset, onSkip, onClose }) => {
  const [passphrase, setPassphrase] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resetting, setResetting] = useState(false);

  const isSetup = status === "needs-setup" || resetting;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (passphrase.length < 8) {
      setError("Use at least 8 characters.");
      return;
    }
    if (isSetup && passphrase !== confirm) {
      setError("Those passphrases do not match.");
      return;
    }
    setBusy(true);
    try {
      if (resetting) await onReset(passphrase);
      else if (status === "needs-setup") await onSetup(passphrase);
      else await onUnlock(passphrase);
      setPassphrase("");
      setConfirm("");
      setResetting(false);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md rounded-3xl border-0 bg-background/80 backdrop-blur-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isSetup ? <ShieldCheck className="h-5 w-5 text-primary" /> : <Lock className="h-5 w-5 text-primary" />}
            {isSetup ? "Create your private passphrase" : "Unlock your history"}
          </DialogTitle>
          <DialogDescription>
            {isSetup
              ? "Your conversations are encrypted on this device before they are saved. Nobody at Mindmail can read them. If you lose this passphrase, your history cannot be recovered by anyone."
              : "Enter the passphrase you created to decrypt your saved conversations."}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-3">
          <Input
            type="password"
            autoFocus
            value={passphrase}
            onChange={(e) => setPassphrase(e.target.value)}
            placeholder="Passphrase"
            className="text-base rounded-2xl border-0 bg-muted/40"
          />
          {isSetup && (
            <Input
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder="Confirm passphrase"
              className="text-base rounded-2xl border-0 bg-muted/40"
            />
          )}
          {error && <p className="text-sm text-destructive">{error}</p>}

          <Button type="submit" disabled={busy} className="w-full rounded-2xl">
            {busy ? "Working..." : isSetup ? "Turn on private history" : "Unlock"}
          </Button>

          <div className="flex items-center justify-between pt-1">
            {status === "needs-setup" && !resetting && (
              <button type="button" onClick={onSkip} className="text-xs text-muted-foreground hover:text-foreground">
                Not now, don't save my chats
              </button>
            )}
            {status === "locked" && (
              <button
                type="button"
                onClick={() => { setResetting((r) => !r); setError(null); }}
                className="text-xs text-muted-foreground hover:text-foreground"
              >
                {resetting ? "Back to unlock" : "Forgot passphrase? Start fresh"}
              </button>
            )}
          </div>
          {resetting && (
            <p className="text-xs text-destructive">
              Starting fresh permanently deletes your saved conversations. They cannot be decrypted without the old passphrase.
            </p>
          )}
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default MindfulVaultDialog;
